/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

/*=========================================================================================
  E-PREVISION Notes
  ----------------------------------------------------------------------------------------
  - viewPermissions: checked when router changes from one another, to see if user has
                     permission to access & view the route displayed.
                     If not allowed -> router pushed to not authorized
                     (Needed for changing from one route to another)
  - editPermission: checked when already in a window - to display edit buttons if user has
                    the specified permission. If not, buttons are not visible.
                    (Needed for special buttons which activate if edit permission - if no
                      buttons needed or only control access, use viewPermissions)

  - rule: checked by ACL checker -> always use role inferior. Permissions are being checked
          additionally for every user. If ACL forbids the URL, it will not be able to be allowed
          by permission.
          Use 'user' preferably, to not have problems with ACL. Unless it is 100% pages we know only
          admins will have access

  - parent: tells NavBar which element it refers to -> avoids loosing focus in navBar when changing
    from one route to another. It is needed for routes which are not
==========================================================================================*/

/* eslint-disable */
import Vue from 'vue'
import Router from 'vue-router'
import auth from './auth/authService'
import oauth2 from '@/http/requests/auth/oauth2/index.js'
import { PRODUCTS_LIST_REPORTS_TAB } from './helpers/productsListTabs'

// import firebase from 'firebase/app'
// import 'firebase/auth'

Vue.use(Router)

// Ir a la ruta en la que ya se está no es un error. Con varias peticiones fallando a la vez por sesión
// cerrada, cada una manda al login y la segunda rompía con NavigationDuplicated (los `.catch()` vacíos no la paran).
const isDuplicated = (err) => err && (err.name === 'NavigationDuplicated' || /redundant navigation/i.test(err.message || ''))
const ignoreDuplicated = (err) => { if (!isDuplicated(err)) return Promise.reject(err) }
const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace
Router.prototype.push = function push (location, onComplete, onAbort) {
  if (onComplete || onAbort) return originalPush.call(this, location, onComplete, onAbort)
  return originalPush.call(this, location).catch(ignoreDuplicated)
}
Router.prototype.replace = function replace (location, onComplete, onAbort) {
  if (onComplete || onAbort) return originalReplace.call(this, location, onComplete, onAbort)
  return originalReplace.call(this, location).catch(ignoreDuplicated)
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [

    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // E-PREVISION - Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/home',
          name: '/',
          meta: {
            rule: 'user',
            authRequired: true
          },
        },
        {
          path: '/home',
          name: 'home',
          component: () => import('./views/Home.vue'),
          meta: {
            rule: 'user',
            authRequired: true
          },
        },

        {
          // Registro de actividad de vendor. Lo sirve go-arpa-api en /api/activity-log
          // y lo escribe su middleware ActivityLogger a partir de las peticiones que
          // llegan con `X-Client-App: vendor` (ver src/axios.js). Es de SOLO LECTURA:
          // un log de auditoría que se pudiera retocar desde el navegador no serviría
          // de nada, así que no hay ruta de alta ni de edición.
          //
          // `viewPermissions` es solo el gate del front (esconde la pantalla y la
          // entrada del menú); quien la exige de verdad es el back, con
          // RequireAnyPermission + el ACL. Los dos nombran el MISMO permiso.
          path: '/activity-log',
          name: 'activity-log',
          component: () => import('./views/eprevision-views/activity-log/ActivityLogList.vue'),
          meta: {
            pageTitle: 'Registro de actividad',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Usuarios'},
              { title: 'Registro de actividad', active: true},
            ],
            rule: 'user',
            viewPermissions:['activityLogView'],
            authRequired: true,
            mode: 'VIEW'
          }
        },
        {
          // Tareas programadas de go-arpa-api (/scheduledJobs). Editar y ejecutar exige
          // además scheduledJobsEdit, que la vista comprueba con hasEditPermissionCode.
          path: '/scheduled-jobs',
          name: 'scheduled-jobs',
          component: () => import('./views/eprevision-views/scheduled-jobs/ScheduledJobsList.vue'),
          meta: {
            pageTitle: 'Tareas programadas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Usuarios'},
              { title: 'Tareas programadas', active: true},
            ],
            rule: 'user',
            viewPermissions:['scheduledJobsView'],
            authRequired: true,
            mode: 'VIEW'
          }
        },
        {
          path: '/user-list',
          name: 'user-list',
          component: () => import('./views/eprevision-views/user/user-list/UserList.vue'),
          meta: {
            pageTitle: 'Lista usuarios',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Usuarios'},
              { title: 'Lista usuarios', active: true},
            ],
            rule: 'user',
            viewPermissions:['UsersEdit'],
            editPermission:['UsersEdit'],
            authRequired: true,
            mode: 'VIEW'
          }
        },
        {
          path: '/payment-form',
          name: 'payment-form',
          component: () => import('./views/eprevision-views/payment-forms/paymentForms.vue'),
          meta: {
            pageTitle: 'Formas de Pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Formas De Pago', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['paymentFormsEdit'],
            editPermission:['paymentFormsEdit'],
          }
        },
        {
          path: '/payment-form/edit/:id',
          name: 'editPaymentForms',
          component: () => import('./views/eprevision-views/payment-forms/editPaymentForms.vue'),
          meta: {
            pageTitle: 'Formas de Pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Formas De Pago', url: '/payment-form'},
              { title: 'Edición', active: true},
            ],
            parent: 'Formas-de-pago',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['paymentFormsEdit'],
            editPermission:['paymentFormsEdit'],
          }
        },
        {
          path: '/civil-status',
          name: 'civil-status',
          component: () => import('./views/eprevision-views/civil-status/civilStatus.vue'),
          meta: {
            pageTitle: 'Estado civil',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Estado civil', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['civilStatusEdit'],
            editPermission:['civilStatusEdit'],
          }
        },
        {
          path: '/collectives',
          name: 'collectives',
          component: () => import('./views/eprevision-views/collectives/collectives.vue'),
          meta: {
            pageTitle: 'Colectivos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Actividades profesionales'},
              { title: 'Colectivos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['colectivesStatusEdit'],
            editPermission:['colectivesStatusEdit'],
          }
        },
        {
          path: '/product-families',
          name: 'product-families',
          component: () => import('./views/eprevision-views/product-families/productFamilies.vue'),
          meta: {
            pageTitle: 'Familia de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Familia de productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productFamiliesEdit'],
            editPermission:['productFamiliesEdit'],
          }
        },
        {
          path: '/taxes',
          name: 'taxes',
          component: () => import('./views/eprevision-views/taxes/taxes.vue'),
          meta: {
            pageTitle: 'Impuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Impuestos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['taxesEdit'],
            editPermission:['taxesEdit'],
          }
        },
        {
          path: '/covers',
          name: 'covers',
          component: () => import('./views/eprevision-views/covers/covers.vue'),
          meta: {
            pageTitle: 'Coberturas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Coberturas ARPA'},
              { title: 'Cobertura', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['coversEdit'],
            editPermission:['coversEdit'],
          }
        },
        {
          path: '/covers/edit/:id',
          name: 'editCovers',
          component: () => import('./views/eprevision-views/covers/editCovers.vue'),
          meta: {
            pageTitle: 'Coberturas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Coberturas', url: '/covers'},
              { title: 'Edición', active: true},
            ],
            parent: 'Coberturas',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['coversEdit'],
            editPermission:['coversEdit'],
          }
        },
        {
          path: '/companies',
          name: 'companies',
          component: () => import('./views/eprevision-views/companies/companies.vue'),
          meta: {
            pageTitle: 'Compañías',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Distribuidoras'},
              { title: 'Compañías' , active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['companiesEdit'],
            editPermission:['companiesEdit'],
          }
        },

        {
          path: '/companyFile/:id',
          name: 'editGeneralReports',
          component: () => import('./views/eprevision-views/generalReports/editGeneralReports.vue'),
          meta: {
            pageTitle: 'Edición - Reportes generales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes' , url: `/products-list?tab=${PRODUCTS_LIST_REPORTS_TAB}`},
              { title: 'Edición - Reportes generales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['companiesEdit'],
            editPermission:['companiesEdit'],
          }
        },
        {
          path: '/general-report-test/:id',
          name: 'general-report-test',
          component: () => import('./views/eprevision-views/generalReports/generalReportsTest.vue'),
          meta: {
            pageTitle: 'Probar - Reportes generales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes' , url: '/products-list'},
              { title: 'Probar - Reportes generales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['companiesEdit'],
            editPermission:['companiesEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/wsCredentials',
          name: 'wsCredentials',
          component: () => import('./views/eprevision-views/wsCredentials/wsCredentials.vue'),
          meta: {
            pageTitle: 'Credenciales webservice',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Aseguradora'},
              { title: 'Credenciales webservice' , active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['wsCredentialsEdit'],
            editPermission:['wsCredentialsEdit'],
          }
        },
        {
          path: '/income-type',
          name: 'income-type',
          component: () => import('./views/eprevision-views/income-type/incomeType.vue'),
          meta: {
            pageTitle: 'Tipo de Ingreso',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Tipo de Ingreso', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['incomeTypeEdit'],
            editPermission:['incomeTypeEdit'],
          }
        },
        {
          path: '/income-type/edit/:id',
          name: 'editIncomeType',
          component: () => import('./views/eprevision-views/income-type/editIncomeType.vue'),
          meta: {
            pageTitle: 'Edición Tipo de Ingreso',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Tipo de Ingreso', url: '/income-type'},
              { title: 'Edición', active: true},
            ],
            parent: 'Tipo de Ingreso',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['incomeTypeEdit'],
            editPermission:['incomeTypeEdit'],
          }
        },
        {
          path: '/medical-test',
          name: 'medical-test',
          component: () => import('./views/eprevision-views/medical-test/medicalTest.vue'),
          meta: {
            pageTitle: 'Pruebas médicas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Pruebas médicas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['medicalTestEdit'],
            editPermission:['medicalTestEdit'],
          }
        },
        {
          path: '/insurance-companies',
          name: 'insurance-companies',
          component: () => import('./views/eprevision-views/insurance-companies/insuranceCompanies.vue'),
          meta: {
            pageTitle: 'Aseguradoras',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Aseguradoras'},
              { title: 'Aseguradora', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['insuranceCompaniesEdit'],
            editPermission:['insuranceCompaniesEdit'],
          }
        },
        // {
        //   path: '/taxes-calculated-on',
        //   name: 'taxes-calculated-on',
        //   component: () => import('./views/eprevision-views/taxes-calculated-on/taxesCalculatedOn.vue'),
        //   meta: {
        //     pageTitle: 'Impuestos',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Productos'},
        //       { title: 'Impuestos', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW',
        //     viewPermissions:['taxesCalculatedOnView'],
        //     editPermission:['taxesCalculatedOnEdit'],
        //   }
        // },
        {
          path: '/sector',
          name: 'sector',
          component: () => import('./views/eprevision-views/sector/sector.vue'),
          meta: {
            pageTitle: 'Sectores',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Actividades profesionales'},
              { title: 'Sectores', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['sectorEdit'],
            editPermission:['sectorEdit'],
          }
        },
        {
          path: '/subcovers',
          name: 'subcovers',
          component: () => import('./views/eprevision-views/subcovers/subcovers.vue'),
          meta: {
            pageTitle: 'SubCoberturas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Coberturas ARPA'},
              { title: 'SubCoberturas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['coversEdit'],
            editPermission:['coversEdit'],
          }
        },
        {
          path: '/subcovers/edit/:id',
          name: 'editSubcovers',
          component: () => import('./views/eprevision-views/subcovers/editSubcovers.vue'),
          meta: {
            pageTitle: 'SubCoberturas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'SubCoberturas', url: '/subcovers'},
              { title: 'Edición', active: true},
            ],
            parent: 'Sub Coberturas',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['coversEdit'],
            editPermission:['coversEdit'],
          }
        },
        {
          path: '/product-categories',
          name: 'product-categories',
          component: () => import('./views/eprevision-views/product-categories/productCategories.vue'),
          meta: {
            pageTitle: 'Categoría de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Categoría de productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productCategoriesEdit'],
            editPermission:['productCategoriesEdit'],
          }
        },
        {
          path: '/product-categories/edit/:id',
          name: 'editProductCategories',
          component: () => import('./views/eprevision-views/product-categories/editProductCategories.vue'),
          meta: {
            pageTitle: 'Categoría de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Categoría de productos', url: '/product-categories'},
              { title: 'Edición', active: true},
            ],
            parent: 'Categoría de Productos',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productCategoriesEdit'],
            editPermission:['productCategoriesEdit'],
          }
        },
        {
          path: '/product',
          name: 'product',
          component: () => import('./views/eprevision-views/product/products.vue'),
          meta: {
            pageTitle: 'Productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            showConfirmation: true,
          }
        },
        {
          path: '/product/:id',
          name: 'productInside',
          component: () => import('./views/eprevision-views/product-inside/productInside.vue'),
          meta: {
            pageTitle: 'Producto',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Productos', url: '/product'},
              { title: 'Producto', active: true},
            ],
            parent: 'Productos',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
          }
        },
        {
          path: '/product/editCover/:id',
          name: 'productEditCover',
          component: () => import('./views/eprevision-views/product-editCover/productEditCover.vue'),
          meta: {
            pageTitle: 'Editar cobertura',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Productos', url: '/product'},
              { title: 'Producto',  url: '/product/:id'},
              { title: 'Editar cobertura', active: true}
            ],
            parent: 'Productos',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            showConfirmation: false,
          }
        },
        {
          path: '/product-edit-file/:id',
          component: () => import('./views/eprevision-views/products/ProductsEdit.vue'),
          meta: {
            pageTitle: 'Edición - Solicitudes de productos',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Productos', url: '/product'},
              { title: 'Producto', url: '/product/:id'},
              { title: 'Edición - Solicitud producto', active: true},
            ],
            parent: 'Productos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            mode: 'VIEW'
          }
        },
        // ESPECIALIDADES RC
        {
          path: '/rc-specialities',
          name: 'rcSpecialities',
          component: () => import('./views/eprevision-views/rc-specialities/rcSpecialities.vue'),
          meta: {
            pageTitle: 'Especialidades RC',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Actividades profesionales'},
              { title: 'Especialidades RC', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['rcSpecialitiesEdit'],
            editPermission:['rcSpecialitiesEdit'],
          }
        },
        // COBERTURAS RC
        {
          path: '/rc-covers',
          name: 'rcCovers',
          component: () => import('./views/eprevision-views/rc-covers/rcCovers.vue'),
          meta: {
            pageTitle: 'Coberturas RC',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Coberturas RC', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['rcCoversEdit'],
            editPermission:['rcCoversEdit'],
          }
        },
        // PRODUCTOS RC
        {
          path: '/rc-product',
          name: 'rcProduct',
          component: () => import('./views/eprevision-views/rc-product/rcProducts.vue'),
          meta: {
            pageTitle: 'Productos RC',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos RC', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['rcProductsEdit'],
            editPermission:['rcProductsEdit'],
          }
        },
        {
          path: '/rc-product/:id',
          name: 'rcProductInside',
          component: () => import('./views/eprevision-views/rc-product-inside/rcProductInside.vue'),
          meta: {
            pageTitle: 'Producto RC',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos RC', url: '/rc-product'},
              { title: 'Producto RC', active: true},
            ],
            parent: 'Productos RC',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['rcProductsEdit'],
            editPermission:['rcProductsEdit'],
          }
        },
        
        {
          path: '/rc-product-edit-file/:id',
          component: () => import('./views/eprevision-views/products/ProductsEdit.vue'),
          meta: {
            pageTitle: 'Edición - Solicitudes de productos RCP',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración' },
              { title: 'Productos RC', url: '/rc-product'},
              { title: 'Producto RC', url: '/rc-product/:id'},
              { title: 'Edición - Solicitud producto RCP', active: true},
            ],
            parent: 'Productos RC',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcProductsEdit'],
            editPermission:['rcProductsEdit'],
            mode: 'VIEW'
          }
        },
        
        {
          path: '/profession',
          name: 'profession',
          component: () => import('./views/eprevision-views/profession/profession.vue'),
          meta: {
            pageTitle: 'Profesión',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Actividades profesionales'},
              { title: 'Profesiones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['professionEdit'],
            editPermission:['professionEdit'],
          }
        },
        {
          path: '/wsprofession',
          name: 'wsProfessions',
          component: () => import('./views/eprevision-views/wsProfessions/wsProfessions.vue'),
          meta: {
            pageTitle: 'Profesión',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Producto', url: '/product'},
              { title: 'Portafolio', url: '/product/:id'},
              { title: 'Profesiones webservice', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['professionEdit'],
            editPermission:['professionEdit'],
          }
        },
        {
          path: '/profession/edit/:id',
          name: 'editProfession',
          component: () => import('./views/eprevision-views/profession/editProfession.vue'),
          meta: {
            pageTitle: 'Profesión',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Productos', url: '/product'},
              { title: 'Profesión', url: '/profession'},
              { title: 'Edición', active: true},
            ],
            parent: 'Profesiones',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['professionEdit'],
            editPermission:['professionEdit'],
          }
        },
        {
          path: '/operators',
          name: 'operators',
          component: () => import('./views/eprevision-views/operators/operators.vue'),
          meta: {
            pageTitle: 'Operadores matemáticos',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Parametros generales'},
              { title: 'Operadores matemáticos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['operatorsEdit'],
            editPermission:['operatorsEdit'],
          }
        },
        {
          path: '/gender',
          name: 'gender',
          component: () => import('./views/eprevision-views/gender/gender.vue'),
          meta: {
            pageTitle: 'Género',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Género', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['gendersEdit'],
            editPermission:['gendersEdit'],
          }
        },
        {
          path: '/aggravatedRisks',
          name: 'aggravatedRisks',
          component: () => import('./views/eprevision-views/aggravatedRisk/aggravatedRisk.vue'),
          meta: {
            pageTitle: 'Deportes',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Deportes', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['aggravatedRisksEdit'],
            editPermission:['aggravatedRisksEdit'],
          }
        },

        {
          path: '/aggravatedRisk/edit/:id',
          name: 'editAggravatedRisks',
          component: () => import('./views/eprevision-views/aggravatedRisk/editAggravatedRisk.vue'),
          meta: {
            pageTitle: 'Riesgos agravados',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Riesgos agravados',  url: '/aggravatedRisks'},
              { title: 'Riesgos agravados - Edición', active: true},
            ],
            parent: 'Riesgos agravados',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['aggravatedRisksEdit'],
            editPermission:['aggravatedRisksEdit'],
          }
        },

        {
          path: '/gender/edit/:id',
          name: 'editGender',
          component: () => import('./views/eprevision-views/gender/editGender.vue'),
          meta: {
            pageTitle: 'Género',
            breadcrumb: [
              { title: 'Home' },
              { title: 'Productos', url: '/product'},
              { title: 'Género', url: '/gender'},
              { title: 'Edición', active: true},
            ],
            parent: 'Género',
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['gendersEdit'],
            editPermission:['gendersEdit'],
          }
        },

        {
          path: '/coverage-chart-covers-list',
          name: 'CoverageChartCoversList',
          component: () => import('./views/eprevision-views/coverage-chart-covers-list/CoverageChartCoversList.vue'),
          meta: {
            pageTitle: 'Cuadro de coberturas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Coberturas ARPA'},
              { title: 'Cuadro de coberturas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['paymentFormsEdit'],
            editPermission:['paymentFormsEdit'],
          }
        },
        // {
        //   path: '/soporte',
        //   name: 'Soporte',
        //   component: () => import('./views/eprevision-views/soporte/soporte.vue'),
        //   meta: {
        //     pageTitle: 'Soporte',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Soporte' , active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW',
        //     viewPermissions:['soporteView'],
        //     editPermission:['soporteEdit'],
        //   }
        // },
        {
          path: '/SEProductCategories',
          name: 'SEProductCategories',
          component: () => import('./views/eprevision-views/SEProductCategories/SEProductCategories.vue'),
          meta: {
            pageTitle: 'SE Categoría de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'SegElevia'},
              { title: 'SE Categoría de productos' , active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['SE_productCategoryEdit'],
            editPermission:['SE_productCategoryEdit'],
          }
        },
        {
          path: '/pensionPercentages',
          name: 'pensionPercentages',
          component: () => import('./views/eprevision-views/pensionPercentages/pensionPercentages.vue'),
          meta: {
            pageTitle: 'Porcentaje - Jubilacion - Años Cotizados',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Seguridad social'},
              { title: 'Porcentaje - Jubilacion - Años Cotizados' , active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['SSpensionPercentagesEdit'],
            editPermission:['SSpensionPercentagesEdit'],
          }
        },
        {
          path: '/delegations',
          name: 'delegations',
          component: () => import('./views/eprevision-views/delegations/delegations.vue'),
          meta: {
            pageTitle: 'Delegaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Delegaciones'},
              { title: 'Delegaciones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            mode: 'VIEW',
            viewPermissions:['delegationsEdit'],
            editPermission:['delegationsEdit'],
          }
        },
        // {
        //   path: '/chat',
        //   redirect: '/chat/all',
        //   name: 'todo2',
        //   component: () => import('./views/eprevision-views/chat/Todo.vue'),
        //   meta: {
        //     pageTitle: 'chat',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'chat' , active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW',
        //   }
        // },
        // {
        //   path: '/chat/:filter',
        //   // redirect: '/chat/all',
        //   name: 'Chat',
        //   component: () => import('./views/eprevision-views/chat/Todo.vue'),
        //   meta: {
        //     pageTitle: 'chat',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'chat' , active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW',
        //   }
        // },
        {
          path: '/user-detail/new',
          name: 'user-new',
          component: () => import('./views/eprevision-views/user/user-edit/UserEdit.vue'),
          meta: {
            rule: 'user',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Lista usuarios', url: '/user-list' },
              { title: 'Nuevo usuario', active: true},
            ],
            viewPermissions:['UsersEdit'],
            editPermission:['UsersEdit'],
            authRequired: true,
            mode: 'NEW'
          }
        },
        {
          path: '/user-detail/view/:id',
          name: 'user-view',
          component: () => import('./views/eprevision-views/user/UserView.vue'),
          meta: {
            pageTitle: 'Usuario',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Lista usuarios', url: '/user-list' },
              { title: 'Detalle usuario', active: true},
            ],
            rule: 'user',
            viewPermissions:['UsersEdit'],
            editPermission:['UsersEdit'],
            authRequired: true,
            mode: 'VIEW'
          }
        },
        {
          path: '/user-detail/edit/:id',
          name: 'user-edit',
          component: () => import('./views/eprevision-views/user/user-edit/UserEdit.vue'),
          meta: {
            pageTitle: 'Editar Usuario',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Lista usuarios', url: '/user-list' },
              { title: 'Editar usuario', active: true},
            ],
            rule: 'user',
            viewPermissions:['UsersEdit'],
            editPermission:['UsersEdit'],
            authRequired: true,
            mode: 'EDIT'
          }
        },
        {
          path: '/role-permissions-list',
          name: 'role-permissions',
          component: () => import('./views/eprevision-views/role-permissions/RolePermissionsList.vue'),
          meta: {
            pageTitle: 'Permisos por rol',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Usuarios'},
              { title: 'Permisos por rol', active: true},
            ],
            rule: 'user',
            viewPermissions:['RolesPermissionsEdit'],
            editPermission:['RolesPermissionsEdit'],
            authRequired: true,
          }
        },

        {
          path: '/user-settings/:id',
          name: 'eprevision-user-settings',
          component: () => import('@/views/pages/e-prevision/user-settings/UserSettings.vue'),
          meta: {
            pageTitle: 'Configuración perfil',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración perfil', active: true},
            ],
            viewPermissions:['ChangePasswordEdit'],
            editPermission:['ChangePasswordEdit'],
            rule: 'user'
          }
        },
      
        {
          path: '/social-security/parameters-list',
          component: () => import('./views/eprevision-views/social-security-parameters/SocialSecurityParametersList.vue'),
          meta: {
            pageTitle: 'Parámetros protección social',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parametros generales'},
              { title: 'Criterios', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSParametersEdit'],
            editPermission:['SSParametersEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-commercials',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercials.vue'),
          meta: {
            pageTitle: 'Comerciales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Comerciales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetUsersEdit'],
            editPermission:['budgetUsersEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-commercials/:id',
          name: 'budget-commercial-detail',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercialDetail.vue'),
          meta: {
            pageTitle: 'Detalle comercial',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Comerciales', url: '/budget-commercials'},
              { title: 'Detalle', active: true},
            ],
            rule: 'user',
            parent: 'Comerciales', // hace que el navBar no se pierda
            authRequired: true,
            viewPermissions:['budgetUsersEdit'],
            editPermission:['budgetUsersEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-dashboard',
          name: 'budget-dashboard',
          component: () => import('./views/eprevision-views/budgets/BudgetDashboard.vue'),
          meta: {
            pageTitle: 'Presupuestos · Dashboard',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos'},
              { title: 'Dashboard', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetView'],
            editPermission:['budgetView'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-commercials/:userId/budget',
          name: 'budget-commercial-budget',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercialBudget.vue'),
          meta: {
            pageTitle: 'Presupuesto del comercial',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Comerciales', url: '/budget-commercials'},
              { title: 'Detalle presupuesto', active: true},
            ],
            parent: 'Comerciales',
            rule: 'user',
            viewPermissions:['budgetUsersEdit'],
            editPermission:['budgetUsersEdit'],
            authRequired: true,
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-dashboard/:userId/budget',
          name: 'budget-dashboard-user-budget',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercialBudget.vue'),
          meta: {
            pageTitle: 'Mi presupuesto',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Presupuestos', url: '/budget-dashboard'},
              { title: 'Mi presupuesto', active: true},
            ],
            parent: 'budget-dashboard',
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetView'],
            editPermission:['budgetView'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-aggregated',
          name: 'budget-aggregated',
          component: () => import('./views/eprevision-views/budgets/BudgetAggregated.vue'),
          meta: {
            pageTitle: 'Presupuesto agregado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Presupuesto agregado', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetAggregatedView'],
            editPermission:['budgetAggregatedView'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-aggregated/:userId/budget',
          name: 'budget-aggregated-user-budget',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercialBudget.vue'),
          meta: {
            pageTitle: 'Presupuesto del comercial',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Presupuesto agregado', url: '/budget-aggregated'},
              { title: 'Presupuesto del comercial', active: true},
            ],
            parent: 'budget-aggregated', // slug del item del navMenu, no el name
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetAggregatedView'],
            editPermission:['budgetAggregatedView'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-objectives-to-sign',
          name: 'budget-objectives-to-sign',
          component: () => import('./views/eprevision-views/budgets/BudgetObjectivesToSign.vue'),
          meta: {
            pageTitle: 'Objetivos pendientes de firma',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Pendientes de firma', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['salesObjectiveView'],
            editPermission:['salesObjectiveSign'],
            mode: 'EDIT'
          }
        },
        {
          // Misma pantalla que el presupuesto del comercial, con la versión enviada a firma.
          path: '/budget-objectives-to-sign/:versionId',
          name: 'budget-objective-to-sign',
          component: () => import('./views/eprevision-views/budgets/BudgetCommercialBudget.vue'),
          meta: {
            pageTitle: 'Presupuesto enviado a firma',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Pendientes de firma', url: '/budget-objectives-to-sign'},
              { title: 'Presupuesto', active: true},
            ],
            parent: 'budget-objectives-to-sign', // slug del item del navMenu
            rule: 'user',
            authRequired: true,
            viewPermissions:['salesObjectiveView'],
            editPermission:['salesObjectiveSign'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-objectives-to-sign/:versionId/compare',
          name: 'budget-objective-to-sign-compare',
          component: () => import('./views/eprevision-views/budgets/BudgetObjectiveCompareView.vue'),
          meta: {
            pageTitle: 'Comparación de presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Panel Comercial'},
              { title: 'Pendientes de firma', url: '/budget-objectives-to-sign'},
              { title: 'Comparación', active: true},
            ],
            parent: 'budget-objectives-to-sign',
            rule: 'user',
            authRequired: true,
            viewPermissions:['salesObjectiveView'],
            editPermission:['salesObjectiveSign'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-convenio',
          component: () => import('./views/eprevision-views/budgets/BudgetConvenio.vue'),
          meta: {
            pageTitle: 'Convenio',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Convenio', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetParametersEdit'],
            editPermission:['budgetParametersEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-candidate-phases',
          component: () => import('./views/eprevision-views/budgets/BudgetCandidatePhases.vue'),
          meta: {
            pageTitle: 'Tiempos candidato',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Tiempos candidato', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetCandidatePhasesEdit'],
            editPermission:['budgetCandidatePhasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-client-phases',
          component: () => import('./views/eprevision-views/budgets/BudgetClientPhases.vue'),
          meta: {
            pageTitle: 'Tiempos clientes',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Tiempos clientes', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetClientPhasesEdit'],
            editPermission:['budgetClientPhasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-products',
          component: () => import('./views/eprevision-views/budgets/BudgetProducts.vue'),
          meta: {
            pageTitle: 'Productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetSEproductCategoriesEdit'],
            editPermission:['budgetSEproductCategoriesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-incentives',
          component: () => import('./views/eprevision-views/budgets/BudgetIncentives.vue'),
          meta: {
            pageTitle: 'Incentivos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Incentivos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['budgetSEproductCategoriesEdit'],
            editPermission:['budgetSEproductCategoriesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/budget-objective-signers',
          name: 'budget-objective-signers',
          component: () => import('./views/eprevision-views/budgets/BudgetObjectiveSigners.vue'),
          meta: {
            pageTitle: 'Firmantes de objetivos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Presupuestos'},
              { title: 'Firmantes', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['salesObjectiveSignersView'],
            editPermission:['salesObjectiveSignersEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/general-contribution-bases',
          component: () => import('./views/eprevision-views/contribution-bases/ContributionBasesListGeneral.vue'),
          meta: {
            pageTitle: 'Régimen General',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Bases de cotización'},
              { title: 'Régimen general', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSContributionBasesEdit'],
            editPermission:['SSContributionBasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/freelance-contribution-bases',
          component: () => import('./views/eprevision-views/contribution-bases/ContributionBasesListFreelance.vue'),
          meta: {
            pageTitle: 'RETA Autónomos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Bases de cotización'},
              { title: 'RETA Autónomos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSContributionBasesEdit'],
            editPermission:['SSContributionBasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/freelance-plain-contribution-bases',
          component: () => import('./views/eprevision-views/contribution-bases/ContributionBasesListFreelancePlain.vue'),
          meta: {
            pageTitle: 'RETA Tarifa Plana',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Bases de cotización'},
              { title: 'RETA Tarifa Plana', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSContributionBasesEdit'],
            editPermission:['SSContributionBasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/contribution-sub-types',
          component: () => import('./views/eprevision-views/contribution-sub-types/ContributionSubTypes.vue'),
          meta: {
            pageTitle: 'Grupos de cotización por tipos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Segurida Social'},
              { title: 'Grupos de cotización'},
              { title: ' Grupos Por tipo', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSContributionSubTypesEdit'],
            editPermission:['SSContributionSubTypesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/mutuas',
          component: () => import('./views/eprevision-views/mutuas/Mutuas.vue'),
          meta: {
            pageTitle: 'Mutuas y prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Mutuas'},
              { title: 'Mutuas y prestaciones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSMutuasEdit'],
            editPermission:['SSMutuasEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/contribution-types',
          component: () => import('./views/eprevision-views/contribution-types/ContributionTypes.vue'),
          meta: {
            pageTitle: 'Grupos de cotización',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Segurida Social'},
              { title: 'Grupos de cotización'},
              { title: 'Grupos', active:true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSContributionTypesEdit'],
            editPermission:['SSContributionTypesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/pension-limits',
          component: () => import('./views/eprevision-views/pension-limits/PensionLimits.vue'),
          meta: {
            pageTitle: 'Límites pensión',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Segurida Social'},
              { title: 'Límites pensión', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSPensionLimitsEdit'],
            editPermission:['SSPensionLimitsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/regulatory-bases',
          component: () => import('./views/eprevision-views/regulatory-bases/RegulatoryBases.vue'),
          meta: {
            pageTitle: 'Bases reguladoras',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Segurida Social'},
              { title: 'Bases reguladoras', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSRegulatoryBasesEdit'],
            editPermission:['SSRegulatoryBasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/regulatory-bases/new',
          component: () => import('./views/eprevision-views/regulatory-bases/RegulatoryBasesDetail.vue'),
          meta: {
            pageTitle: 'Nueva base reguladora',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Bases reguladoras',  url: '/social-security/regulatory-bases' },
              { title: 'Nueva base reguladora', active: true},
            ],
            parent: 'Bases reguladoras',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSRegulatoryBasesEdit'],
            editPermission:['SSRegulatoryBasesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/advised-needs',
          component: () => import('./views/eprevision-views/advised-needs/AdvisedNeeds.vue'),
          meta: {
            pageTitle: 'Necesidades recomendadas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Necesidades recomendadas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSAdvisedNeedsEdit'],
            editPermission:['SSAdvisedNeedsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/social-security-benefits',
          component: () => import('./views/eprevision-views/social-security-benefits/SocialSecurityBenefits.vue'),
          meta: {
            pageTitle: 'Prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Seguridad social'},
              { title: 'Prestaciones protección social', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSBenefitsEdit'],
            editPermission:['SSBenefitsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/mutua-benefits',
          component: () => import('./views/eprevision-views/mutua-benefits/MutuaBenefits.vue'),
          meta: {
            pageTitle: 'Prestaciones mutuas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Mutuas'},
              { title: 'Prestaciones mutuas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSMutuaBenefitsEdit'],
            editPermission:['SSMutuaBenefitsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/social-security/mutua-benefits/new',
          component: () => import('./views/eprevision-views/mutua-benefits/MutuaBenefitsDetail.vue'),
          meta: {
            pageTitle: 'Prestaciones mutuas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Prestaciones mutuas',  url: '/social-security/regulatory-bases'},
              { title: 'Nueva prestación', active: true},
            ],
            parent: 'Prestaciones mutua',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSMutuaBenefitsEdit'],
            editPermission:['SSMutuaBenefitsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/coverage-chart-SE-covers-list',
          component: () => import('./views/eprevision-views/coverage-chart-SE-covers/CoverageChartSECoversList.vue'),
          meta: {
            pageTitle: 'Cuadro de coberturas SegElevia: Garantías - prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'SegElevia'},
              { title: 'Cuadro coberturas SegElevia', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SE_CoverageChartEdit'],
            editPermission:['SE_CoverageChartEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/SE-product-family-list',
          component: () => import('./views/eprevision-views/product-families-SE/productFamiliesSEList.vue'),
          meta: {
            pageTitle: 'Familia de productos SegElevia',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'SegElevia'},
              { title: 'Familia de productos SegElevia', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SE_ProductFamilyEdit'],
            editPermission:['SE_ProductFamilyEdit'],
            mode: 'EDIT'
          }
        },
        // VIEJAS CONSULTAS DE CUADRO DE COBERTURAS ETC; ANTES DE HABELRO METIDO EN EL CLIENTE
        // {
        //   path: '/SE-cover-chart',
        //   component: () => import('./views/eprevision-views/reports/ReportCoverChart.vue'),
        //   meta: {
        //     pageTitle: 'Cuadro coberturas',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'SegElevia'},
        //       { title: 'Cuadro coberturas SegElevia', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['SE_CoverChartReport'],
        //     editPermission:['SE_CoverChartReport'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/SE-policies',
        //   component: () => import('./views/eprevision-views/reports/ReportPolicies.vue'),
        //   meta: {
        //     pageTitle: 'Relación de pólizas',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'SegElevia'},
        //       { title: 'Relación de pólizas SegElevia', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['SE_PoliciesReport'],
        //     editPermission:['SE_PoliciesReport'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/SE-receipts',
        //   component: () => import('./views/eprevision-views/reports/ReportReceipts.vue'),
        //   meta: {
        //     pageTitle: 'Recibos pagados',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'SegElevia'},
        //       { title: 'Recibos pagados SegElevia', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['SE_ReceiptsReport'],
        //     editPermission:['SE_ReceiptsReport'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/virtual-bucket',
        //   component: () => import('./views/eprevision-views/virtual-bucket/VirtualBucket.vue'),
        //   meta: {
        //     pageTitle: 'Cubeta virtual',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Proyectos', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/virtual-bucket/:id',
        //   component: () => import('./views/eprevision-views/virtual-bucket/VirtualBucketDetail.vue'),
        //   meta: {
        //     pageTitle: 'Detalle cubeta',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Proyectos', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        {
          path: '/projects',
          component: () => import('./views/eprevision-views/client-projects/ClientSearch.vue'),
          meta: {
            pageTitle: 'Buscar candidatos / clientes',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado', active: true},
              { title: 'Buscar candidatos / clientes', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        // OLD WAY OF FILLING CLIENT FORM - keys extracted from PDF
        // {
        //   path: '/projects/client/fillForm/:fileId',
        //   name: 'client-fill-form',
        //   component: () => import('./views/eprevision-views/client-projects/ClientFillForm_NOBORRAR.vue'),
        //   meta: {
        //     pageTitle: 'Candidato / cliente',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Mercado',  url: '/projects'},
        //       { title: 'Cliente / Candidato',  url: '/projects/client'},
        //       { title: 'Rellenado documento', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['clientProjectsView'],
        //     editPermission:['clientProjectsEdit'],
        //     mode: 'EDIT'
        //   }
        // },
        // NEW WAY OF FILLING CLIENT FORM - loading PDF and filling it directly
        {
          path: '/projects/client/fillForm/:fileName/:fileId',
          name: 'client-fill-form-file',
          component: () => import('./views/eprevision-views/client-projects/ClientFillFormFile.vue'),
          meta: {
            pageTitle: 'Candidato / cliente',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Rellenado documento', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        // INTENTO DE MWETERLO TODO EN UNA PESTAÑA - DEJADO A MEDIAS. DEJAR? BORRAR?
        // {
        //   path: '/projects/client/fillForms',
        //   name: 'client-fill-form-files',
        //   component: () => import('./views/eprevision-views/client-projects/ClientFillFilesReviewCovers.vue'),
        //   props: true, // enable route params -> props
        //   meta: {
        //     pageTitle: 'Candidato / cliente',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Mercado',  url: '/projects'},
        //       { title: 'Cliente / Candidato',  url: '/projects/client'},
        //       { title: 'Rellenado documento', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['clientProjectsView'],
        //     editPermission:['clientProjectsEdit'],
        //     mode: 'EDIT'
        //   }
        // },
        {
          path: '/projects/client',
          name: 'client',
          component: () => import('./views/eprevision-views/client-projects/Client.vue'),
          meta: {
            pageTitle: 'Candidato / cliente',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client-tarification',
          name: 'client-tarification',
          component: () => import('./views/eprevision-views/client-projects/ClientData_NEW.vue'),
          meta: {
            pageTitle: 'Candidato / cliente',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientTarificationsView'],
            editPermission:['clientTarificationsEdit'],
            mode: 'EDIT'
          }
        },

        // ------------------------------------------------------------------
        // NUEVOS CLIENTE
        // {
        //   path: '/projects/calculators/:calculatorType',
        //   name: 'generic-calculator',
        //   component: () => import('./views/eprevision-views/product-calculators/CalculatorSelector.vue'),
        //   meta: {
        //     pageTitle: 'Calculadoras',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Calculadoras genéricas', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['productsCalculator'],
        //     editPermission:['productsCalculator'],
        //     mode: 'EDIT'
        //   }
        // },
        // LIBRE
        {
          path: '/projects/client/review-project',
          name: 'client-review-project',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Resumen prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Calculadoras producto', url: '/projects/client/calculators'},
              { title: 'Resumen prestaciones', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },

        // Genérico
        {
          path: '/projects/client/calculators',
          name: 'client-product-calculator',
          component: () => import('./views/eprevision-views/calculation-process/ProductsCalculators_v2.vue'),
          meta: {
            pageTitle: 'Calculadoras',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Calculadoras producto', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/covered-results',
          name: 'view-covers-donuts',
          component: () => import('./views/eprevision-views/calculation-process/CoversDonuts_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Calculadoras producto', url: '/projects/client/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/review-project'},
              { title: 'Resultados', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },

        {
          path: `/projects/client/insured-form`,
          name: 'client-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Resumen prestaciones', url: '/projects/client/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },

        {
          path: '/projects/client/policyholder-form',
          name: 'client-policyholder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Resumen prestaciones', url: '/projects/client/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },

        {
          path: '/projects/client/health-questionnaire',
          name: 'client-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Resumen prestaciones', url: '/projects/client/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/health-questionnaires',
          name: 'client-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Resumen prestaciones', url: '/projects/client/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/insured-form'},
              { title: 'Datos tomador',url: '/projects/client/policyholder-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/payment-details',
          name: 'client-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Datos asegurado', url: '/projects/client/insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/additional-questions',
          name: 'client-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Datos asegurado', url: '/projects/client/insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },

        // -------------------- SOCIAL - CLIENTES --------------------

        {
          path: '/projects/client/social-security/results',
          name: 'client-social-security-results',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityResults_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/needs',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityNeeds_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/calculators',
          name: 'client-social-security-calculators',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityCalculators_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/review-project',
          name: 'client-review-project-social',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Resumen prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        // OKAY
        {
          path: '/projects/client/social-security/covered-results',
          name: 'ss-view-covers-donuts',
          component: () => import('./views/eprevision-views/calculation-process/CoversDonuts_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Resultados', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },

        {
          path: `/projects/client/social-security/insured-form`,
          name: 'ss-projects-client-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/policyholder-form',
          name: 'ss-projects-client-policyholder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/social-security/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },

        {
          path: '/projects/client/social-security/health-questionnaire',
          name: 'ss-projects-client-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/social-security/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/health-questionnaires',
          name: 'ss-projects-client-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/social-security/insured-form'},
              { title: 'Datos tomador', url: '/projects/client/social-security/policyholder-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/payment-details',
          name: 'ss-projects-client-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/social-security/insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/social-security/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/social-security/additional-questions',
          name: 'ss-projects-client-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cálculo protección social',  url: '/projects/client/social-security/results'},
              { title: 'Necesidades',  url: '/projects/client/social-security/needs'},
              { title: 'Calculadoras', url:'/projects/client/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/projects/client/social-security/review-project'},
              { title: 'Datos asegurado', url: '/projects/client/social-security/insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/social-security/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },


        // -------------------- SOCIAL - CLIENTES --------------------

        // ---------------------- PRESUPUESTOS MODIFICADO -- flujo único ----------------------
         {
          path: '/plans',
          component: () => import('./views/eprevision-views/projectsAll/ProjectsAll_v2.vue'),
          meta: {
            pageTitle: 'Presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado'},
              { title: 'Presupuestos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsEdit'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        // PRESUPUESTOS - LIBRE
        {
          path: '/plans/project/review-project',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado'},
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/calculator',
          component: () => import('./views/eprevision-views/calculation-process/ProductsCalculators_v2.vue'),
          meta: {
            pageTitle: 'Presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado'},
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Calculadoras ', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/covered-results',
          name: 'plans-covers-donuts',
          component: () => import('./views/eprevision-views/calculation-process/CoversDonuts_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado'},
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Resultados', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/project/insured-form',
          name: 'plans-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/plans/project/policy-holder',
          name: 'plans-policy-holder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', url: '/plans/project/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/project/health-questionnaire',
          name: 'plans-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Preguntas de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', url: '/plans/project/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/project/health-questionnaires',
          name: 'plans-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Preguntas de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', url: '/plans/project/insured-form'},
              { title: 'Datos tomador', url: '/plans/project/policy-holder'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/project/payment-details',
          name: 'plans-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', url: '/plans/project/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/project/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/project/additional-questions',
          name: 'plans-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/project/review-project'},
              { title: 'Datos asegurado', url: '/plans/project/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/project/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        // LIBRE
        // SOCIAL
        {
          path: '/plans/social-security/review-project',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado'},
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Resumen prestaciones', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/plans/social-security/calculator',
          name: 'plans-social-security-calculators',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityCalculators_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadoras', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/covered-results',
          name: 'plans-covered-results',
          component: () => import('./views/eprevision-views/calculation-process/CoversDonuts_v2.vue'),
          meta: {
            pageTitle: 'Graficos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Gráficos coberturas', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: `/plans/social-security/insured-form`,
          name: 'plans-social-security-calculator-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/policyholder-form',
          name: 'plans-calculator-policyholder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', url: '/plans/social-security/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/health-questionnaires',
          name: 'plans-social-security-calculator-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', url: '/plans/social-security/insured-form'},
              { title: 'Datos tomador',  url: '/plans/social-security/policyholder-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/health-questionnaire',
          name: 'plans-social-security-calculator-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', url: '/plans/social-security/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/payment-details',
          name: 'plans-social-security-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', url: '/plans/social-security/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/social-security/health-questionnaires'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/additional-questions',
          name: 'plans-social-security-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', url: '/plans/social-security/needs'},
              { title: 'Calculadora', url: '/plans/social-security/calculator'},
              { title: 'Resumen prestaciones', url: '/plans/social-security/review-project'},
              { title: 'Datos asegurado', url: '/plans/social-security/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/social-security/health-questionnaires'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['clientProjectsView'],
            editPermission:['clientProjectsEdit'],
            mode: 'EDIT'
          }
        },
        // REISAR POR QUE FALLAN :
        {
          path: '/plans/social-security/results',
          name: 'plans-social-security-results',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityResults_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              // { title: 'Datos', url: '/plans/social-security/input-form'},
              { title: 'Prestaciones', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:[],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/social-security/needs',
          name: 'plans-social-security-needs',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityNeeds_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Prestaciones', url: '/plans/social-security/results'},
              { title: 'Necesidades', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:[],
            mode: 'EDIT'
          }
        },


        // ---------------------- PRESUPUESTOS MODIFICADO -- flujo único ----------------------

        // ---------------------- CALCULADORAS LIBRES ! -- flujo único ----------------------
        // NO DESDE CANDIDATOS
        {
          path: '/products/calculator',
          name: 'products-calculator',
          component: () => import('./views/eprevision-views/product-calculators/ProductCalculators_v2.vue'),
          meta: {
            pageTitle: 'Productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadoras' },
              { title: 'Calculadora productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },

        // LIBRE
        {
          path: '/products/review-project',
          name: 'products-review-project',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Resumen prestaciones',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: `/products/calculator/insured-form`,
          name: 'products-calculator-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadoras', url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/calculator/policyholder-form',
          name: 'products-calculator-policyholder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', url: '/products/calculator/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },

        {
          path: '/products/calculator/health-questionnaire',
          name: 'products-calculator-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', url: '/products/calculator/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/calculator/health-questionnaires',
          name: 'products-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', url: '/products/calculator/insured-form'},
              { title: 'Datos tomador', url: '/products/calculator/policyholder-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/calculator/payment-details',
          name: 'products-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', url: '/products/calculator/insured-form'},
              { title: 'Datos tomador', url: '/products/calculator/policyholder-form'},
              { title: 'Preguntas de salud', url: '/products/calculator/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/calculator/additional-questions',
          name: 'products-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora productos',  url: '/products/calculator'},
              { title: 'Resumen prestaciones', url: '/products/review-project'},
              { title: 'Datos asegurado', url: '/products/calculator/insured-form'},
              { title: 'Datos tomador', url: '/products/calculator/policyholder-form'},
              { title: 'Preguntas de salud', url: '/products/calculator/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },


        // SOCIAL:
        {
          path: '/products/social-security/input-form',
          name: 'social-security-calculator',
          component: () => import('./views/eprevision-views/social-security/SocialSecurityCalculator_v2.vue'),
          meta: {
            pageTitle: 'Protección social',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadoras'},
              { title: 'Protección social', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:['SSCalculator'],
            mode: 'EDIT'
          }
        },

        {
          path: '/products/social-security/results',
          name: 'social-security-results',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityResults_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:[],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/needs',
          name: 'social-security-needs',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityNeeds_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSCalculator'],
            editPermission:[],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculators',
          component: () => import('./views/eprevision-views/calculation-process/SocialSecurityCalculators_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social' },
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculators/review-project',
          component: () => import('./views/eprevision-views/calculation-process/ReviewProject_v2.vue'),
          meta: {
            pageTitle: 'Productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/covered-results',
          name: 'social-security-covered-results',
          component: () => import('./views/eprevision-views/calculation-process/CoversDonuts_v2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Resultados', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: `/products/social-security/calculator/insured-form`,
          name: 'social-security-calculator-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculator/policyholder-form',
          name: 'calculator-policyholder-form',
          component: () => import('./views/eprevision-views/calculation-process/PolicyholderForm_v2.vue'),
          meta: {
            pageTitle: 'Datos tomador',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', url: '/products/social-security/calculator/insured-form'},
              { title: 'Datos tomador', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculator/health-questionnaires',
          name: 'social-security-calculator-health-questionnaires',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', url: '/products/social-security/calculator/insured-form'},
              { title: 'Datos tomador',  url: '/products/social-security/calculator/policyholder-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculator/health-questionnaire',
          name: 'social-security-calculator-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/HealthQuestionnaire_v2.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', url: '/products/social-security/calculator/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculator/payment-details',
          name: 'social-security-calculator-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', url: '/products/social-security/calculator/insured-form'},
              { title: 'Datos tomador',  url: '/products/social-security/calculator/policyholder-form'},
              { title: 'Preguntas de salud', url: '/products/social-security/calculator/health-questionnaires'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/social-security/calculator/additional-questions',
          name: 'social-security-calculator-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          // component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestions.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Protección social'},
              { title: 'Datos', url: '/products/social-security/input-form'},
              { title: 'Prestaciones', url: '/products/social-security/results'},
              { title: 'Necesidades', url: '/products/social-security/needs'},
              { title: 'Calculadora', url: '/products/social-security/calculators'},
              { title: 'Resumen prestaciones', url: '/products/social-security/calculators/review-project'},
              { title: 'Datos asegurado', url: '/products/social-security/calculator/insured-form'},
              { title: 'Datos tomador',  url: '/products/social-security/calculator/policyholder-form'},
              { title: 'Preguntas de salud', url: '/products/social-security/calculator/health-questionnaires'},
              { title: 'Preguntas adicionales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsCalculator'],
            editPermission:['productsCalculator'],
            mode: 'EDIT'
          }
        },

        // RC 

        // FLUJO RC DESDE CALCULADORA (LIBRE)
        {
          path: '/products/rc-calculator',
          component: () => import('./views/eprevision-views/rc-simulator/rcSimulatorCalculator.vue'),
          meta: {
            pageTitle: 'Responsabilidad civil',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'VIEW',
            compactLayout: true
          }
        },
        {
          path: `/products/rc-calculator/insured-form`,
          name: 'rc-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/products/rc-calculator'},
              { title: 'Datos asegurado', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/rc-calculator/health-questionnaire',
          name: 'rc-calculator-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/products/rc-calculator'},
              { title: 'Datos asegurado', url: '/products/rc-calculator/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/rc-calculator/payment-details',
          name: 'rc-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/products/rc-calculator'},
              { title: 'Datos asegurado', url: '/products/rc-calculator/insured-form'},
              { title: 'Preguntas de salud', url: '/products/rc-calculator/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/products/rc-calculator/additional-questions',
          name: 'rc-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/products/rc-calculator'},
              { title: 'Datos asegurado', url: '/products/rc-calculator/insured-form'},
              { title: 'Preguntas de salud', url: '/products/rc-calculator/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },

        // FLUJO RC DESDE PRESUPUESTO
        {
          path: '/plans/rc-project/review-project',
          component: () => import('./views/eprevision-views/rc-simulator/rcSimulatorCalculator.vue'),
          meta: {
            pageTitle: 'Presupuestos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Proyecto RC', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'VIEW',
            compactLayout: true
          }
        },
        {
          path: `/plans/rc-project/insured-form`,
          name: 'rc-plans-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Responsabilidad civil',  url: '/plans/rc-project/review-project'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/rc-project/health-questionnaire',
          name: 'rc-plans-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Presupuestos', url: '/plans'},
              { title: 'Responsabilidad civil',  url: '/plans/rc-project/review-project'},
              { title: 'Datos asegurado', url: '/plans/rc-project/insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/rc-project/payment-details',
          name: 'rc-plans-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/plans/rc-project/review-project'},
              { title: 'Datos asegurado', url: '/plans/rc-project/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/rc-project/health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/plans/rc-project/additional-questions',
          name: 'rc-plans-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Responsabilidad civil',  url: '/plans/rc-project/review-project'},
              { title: 'Datos asegurado', url: '/plans/rc-project/insured-form'},
              { title: 'Preguntas de salud', url: '/plans/rc-project/health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'Presupuestos',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },

        // FLUJO RC CLIENTES
        {
          path: '/projects/client/rc-calculators',
          name: 'client-rc-product-calculator',
          component: () => import('./views/eprevision-views/rc-simulator/rcSimulatorCalculator.vue'),
          meta: {
            pageTitle: 'Responsabilidad civil',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Responsabilidad civil', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: `/projects/client/rc-insured-form`,
          name: 'rc-client-insured-form',
          component: () => import('./views/eprevision-views/calculation-process/InsuredForm_v2.vue'),
          meta: {
            pageTitle: 'Datos asegurado',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Responsabilidad civil', url: '/projects/client/rc-calculators'},
              { title: 'Datos asegurado', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/rc-health-questionnaire',
          name: 'rc-client-health-questionnaire',
          component: () => import('./views/eprevision-views/calculation-process/HealthQuestionFiles.vue'),
          meta: {
            pageTitle: 'Cuestionario de salud',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Responsabilidad civil', url: '/projects/client/rc-calculators'},
              { title: 'Datos asegurado', url: '/projects/client/rc-insured-form'},
              { title: 'Preguntas de salud', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/rc-payment-details',
          name: 'rc-client-beneficiaries-payment-details',
          component: () => import('./views/eprevision-views/calculation-process/Beneficiaries_v2.vue'),
          meta: {
            pageTitle: 'Forma de pago',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Datos asegurado', url: '/projects/client/rc-insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/rc-health-questionnaire'},
              { title: 'Beneficiarios e IBAN', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },
        {
          path: '/projects/client/rc-additional-questions',
          name: 'rc-client-beneficiaries-additional-questions',
          component: () => import('./views/eprevision-views/calculation-process/AdditionalQuestionsFiles.vue'),
          meta: {
            pageTitle: 'Preguntas adicionales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado',  url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Datos asegurado', url: '/projects/client/rc-insured-form'},
              { title: 'Preguntas de salud', url: '/projects/client/rc-health-questionnaire'},
              { title: 'Preguntas adicionales', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['rcCalculator'],
            editPermission:['rcCalculator'],
            mode: 'EDIT'
          }
        },

        // ------------------------------------------------------------------
        // ------------------------------------------------------------------
        // {
        //   path: '/projects/virtualBucket',
        //   component: () => import('./views/eprevision-views/client-projects/VirtualBucketList.vue'),
        //   meta: {
        //     pageTitle: 'Cubeta virtual',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Presupuestos', url: '/projects'},
        //       { title: 'Cliente / Candidato',  url: '/projects/client'},
        //       { title: 'Cubeta virtual', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['VirtualBucketView'],
        //     editPermission:['VirtualBucketEdit'],
        //     mode: 'VIEW'
        //   }
        // },
        {
          path: '/projects/client/virtualBucket/:id',
          component: () => import('./views/eprevision-views/client-projects/VirtualBucketListEdit.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mercado', url: '/projects'},
              { title: 'Cliente / Candidato',  url: '/projects/client'},
              { title: 'Cubeta virtual', url: '/projects/client'},
              { title: 'Cubeta virtual - Edición', active: true},
            ],
            parent: 'projects',
            rule: 'user',
            authRequired: true,
            viewPermissions:['VirtualBucketView'],
            editPermission:['VirtualBucketEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/template-list',
          component: () => import('./views/eprevision-views/templates/TemplatesList.vue'),
          meta: {
            pageTitle: 'Plantillas informes',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Plantillas informes',  active: true},
            ],
            rule: 'user',
            parent:'templates',
            authRequired: true,
            viewPermissions:['pdfTemplatesEdit'],
            editPermission:['pdfTemplatesEdit'],

          }
        },
        {
          path: '/template-new',
          component: () => import('./views/eprevision-views/templates/TemplatesDetail.vue'),
          meta: {
            pageTitle: 'Nueva plantilla',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Plantillas informes', url: '/template-list'},
              { title: 'Nueva plantilla', active: true},
            ],
            rule: 'user',
            parent:'templates',
            authRequired: true,
            viewPermissions:['pdfTemplatesView'],
            editPermission:['pdfTemplatesEdit'],
            mode: 'NEW'
          }
        },
        {
          path: '/template-detail/view/:id',
          component: () => import('./views/eprevision-views/templates/TemplatesDetail.vue'),
          meta: {
            pageTitle: 'Detalle plantilla',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Plantillas informes', url: '/template-list'},
              { title: 'Vista plantilla', active: true},
            ],
            rule: 'user',
            parent:'templates',
            authRequired: true,
            viewPermissions:['pdfTemplatesView'],
            editPermission:['pdfTemplatesEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/template-detail/edit/:id',
          component: () => import('./views/eprevision-views/templates/TemplatesDetail.vue'),
          meta: {
            pageTitle: 'Detalle plantilla',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Plantillas informes', url: '/template-list'},
              { title: 'Edición plantilla', active: true},
            ],
            rule: 'user',
            parent:'templates',
            authRequired: true,
            viewPermissions:['pdfTemplatesView'],
            editPermission:['pdfTemplatesEdit'],
            mode: 'EDIT'
          }
        },
        {
          path: '/file-keys-help',
          name: 'FileKeysHelp',
          component: () => import('./views/eprevision-views/products/FileKeysHelp.vue'),
          meta: {
            pageTitle: 'Solicitudes',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes', url: '/products-list' },
              { title: 'Información claves',active: true },
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/products-list',
          component: () => import('./views/eprevision-views/forms/forms.vue'),
          meta: {
            pageTitle: 'Solicitudes',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes', active: true },
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/products-edit-file/:id',
          component: () => import('./views/eprevision-views/products/ProductsEdit.vue'),
          meta: {
            pageTitle: 'Edición - Solicitudes de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes', url: '/products-list'},
              { title: 'Edición - Solicitudes de productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/products-detail-test/:id',
          name: 'products-detail-test',
          component: () => import('./views/eprevision-views/products/ProductsTestHealthyForm.vue'),
          meta: {
            pageTitle: 'Solicitudes de productos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Productos'},
              { title: 'Solicitudes', url: '/products-list'},
              { title: 'Solicitudes de productos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['productsEdit'],
            editPermission:['productsEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/healthCheckers',
          component: () => import('./views/eprevision-views/healthCheckers/HealthCheckers.vue'),
          meta: {
            pageTitle: 'Estado de los Servicios',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Estado de los Servicios', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['healthCheckerView'],
            editPermission:['healthCheckerView'],
            mode: 'VIEW'
          }
        },

        {
          path: '/segelevia-visits',
          component: () => import('./views/eprevision-views/segelevia-visits/SegeleviaVisits.vue'),
          meta: {
            pageTitle: 'Actividad comercial - Visitas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Actividad comercial - Visitas', active: true },
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['controlPanelSegElevia'],
            editPermission:['controlPanelSegElevia'],
            mode: 'VIEW'
          }
        },

        {
          path: '/socialSecurityDistributionComplements',
          component: () => import('./views/eprevision-views/socialSecurityDistributionComplements/SocialSecurityDistributionComplements.vue'),
          meta: {
            pageTitle: 'Seguridad social',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Parámetros generales'},
              { title: 'Capitales complementarios', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['SSpensionPercentagesEdit'],
            editPermission:['SSpensionPercentagesEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/cities',
          component: () => import('./views/eprevision-views/city/cities.vue'),
          meta: {
            pageTitle: 'Ciudades',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Localizaciones'},
              { title: 'Ciudades', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['citiesEdit'],
            editPermission:['citiesEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/provinces',
          component: () => import('./views/eprevision-views/province/provinces.vue'),
          meta: {
            pageTitle: 'Provincias',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Localizaciones'},
              { title: 'Provincias', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['provincesEdit'],
            editPermission:['provincesEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/postalCodes',
          component: () => import('./views/eprevision-views/postal-code/postalCodes.vue'),
          meta: {
            pageTitle: 'Códigos postales',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Localizaciones'},
              { title: 'Códigos postales', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['postalCodesEdit'],
            editPermission:['postalCodesEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/countries',
          component: () => import('./views/eprevision-views/countries/countries.vue'),
          meta: {
            pageTitle: 'Países',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Configuración'},
              { title: 'Localizaciones'},
              { title: 'Países', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['countriesEdit'],
            editPermission:['countriesEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/virtualBucket',
          component: () => import('./views/eprevision-views/virtualBucket/virtualBucket.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['VirtualBucketView'],
            editPermission:['VirtualBucketEdit'],
            mode: 'VIEW'
          }
        },


        {
          path: '/virtualBucketStatistics',
          component: () => import('./views/eprevision-views/virtualBuckeStatistics/virtualBucketStatistics.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', url: '/virtualBucket' },
              { title: ' Estadísticas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['virtualStatisticsView'],
            editPermission:['virtualStatisticsView'],
            mode: 'VIEW'
          }
        },
        {
          path: '/virtualBucketFilterStatistics',
          component: () => import('./views/eprevision-views/virtualBuckeStatistics/virtualBucketFilter.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', url: '/virtualBucket' },
              { title: ' Estadísticas', url: '/virtualBucketStatistics'},
              { title: ' Listado', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['virtualStatisticsView'],
            editPermission:['virtualStatisticsView'],
            mode: 'VIEW'
          }
        },
        {
          path: '/virtualBucketFilterStatistics/:id',
          component: () => import('./views/eprevision-views/virtualBucket/virtualBucketEdit.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', url: '/virtualBucket' },
              { title: ' Estadísticas', url: '/virtualBucketStatistics'},
              { title: ' Listado', url: '/virtualBucketFilterStatistics'},
              { title: ' Vista', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['virtualStatisticsView'],
            editPermission:['virtualStatisticsView'],
            mode: 'VIEW'
          }
        },
        {
          path: '/virtualBucket/:id',
          component: () => import('./views/eprevision-views/virtualBucket/virtualBucketEdit.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', url: '/virtualBucket'},
              { title: ' Editar', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['VirtualBucketView'],
            editPermission:['VirtualBucketEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/virtualBucketFilter',
          component: () => import('./views/eprevision-views/virtualBucket/virtualBucketFilter.vue'),
          meta: {
            pageTitle: 'Cubeta virtual',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Cubeta virtual', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['VirtualBucketView'],
            editPermission:['VirtualBucketEdit'],
            mode: 'VIEW'
          }
        },

        {
          path: '/documents',
          component: () => import('./views/eprevision-views/documents/documents.vue'),
          meta: {
            pageTitle: 'Documentos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Documentos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['fileFormsView'],
            editPermission:['fileFormsEdit'],
            mode: 'VIEW'
          }
        },
        // OLD WAY OF FILLING CLIENT FORM - keys extracted from PDF
        // {
        //   path: '/documents/fillForm/:fileId',
          // component: () => import('./views/eprevision-views/documents/DocumentsFillForm.vue'),
        //   meta: {
        //     pageTitle: 'Documentos',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Documentos', url: '/documents'},
        //       { title: 'Rellenado documento', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['fileFormsView'],
        //     editPermission:['fileFormsEdit'],
        //     mode: 'VIEW'
        //   }
        // },
        // NEW WAY OF FILLING CLIENT FORM - loading PDF and filling it directly
        {
          path: '/documents/fillForm/:fileName/:fileId',
          name: 'documents-fill-form-file',
          component: () => import('./views/eprevision-views/documents/DocumentsFillFormFile.vue'),
          meta: {
            pageTitle: 'Documentos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Documentos', url: '/documents'},
              { title: 'Rellenado documento', active: true},
            ],
            rule: 'user',
            parent: 'Documentos',
            authRequired: true,
            viewPermissions:['fileFormsView'],
            editPermission:['fileFormsEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/externalLinks',
          component: () => import('./views/eprevision-views/externalLinks/externalLinks.vue'),
          meta: {
            pageTitle: 'Enlaces externos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Enlaces externos', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['externalLinksView'],
            editPermission:['externalLinksEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/mortgage-simulator',
          component: () => import('./views/eprevision-views/mortgage-insurance/simuladorHipoteca.vue'),
          meta: {
            pageTitle: 'Seguro de vida hipotecas',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calculadora hipotecas', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['mortgageView'],
            editPermission:['mortgageView'],
            mode: 'VIEW'
          }
        },
        
        // {
        //   path: '/web-leads',
        //   component: () => import('./views/eprevision-views/leads/leads.vue'),
        //   meta: {
        //     pageTitle: 'Leads',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Leads', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['webLeadsEdit'],
        //     editPermission:['webLeadsEdit'],
        //     mode: 'VIEW'
        //   }
        // },
        //         {
        //   path: '/web-leads/:id',
        //   component: () => import('./views/eprevision-views/leads/leadsDetail.vue'),
        //   meta: {
        //     pageTitle: 'Leads - Edición',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Leads', url: '/web-leads'},
        //       { title: 'Leads - Edición', active: true},
        //     ],
        //     rule: 'user',
        //     authRequired: true,
        //     viewPermissions:['webLeadsEdit'],
        //     editPermission:['webLeadsEdit'],
        //     mode: 'VIEW'
        //   }
        // },

        {
          path: '/externalLinkEdit/:id',
          component: () => import('./views/eprevision-views/externalLinks/externalLinkEdit.vue'),
          meta: {
            pageTitle: 'Enlaces externos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Enlaces externos', url: '/externalLinks'},
              { title: 'Enlaces externos - Edición', active: true},
            ],
            rule: 'user',
            authRequired: true,
            viewPermissions:['externalLinksEdit'],
            editPermission:['externalLinksEdit'],
            mode: 'VIEW'
          }
        },
        {
          path: '/chatPDF/:id',
          component: () => import('./views/eprevision-views/chatPDF/chatPDF.vue'),
          meta: {
            pageTitle: 'PreviBot documentos',
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Documentos', url: '/documents'},
              { title: 'PreviBot', active: true},
            ],
            rule: 'user',
            authRequired: true,
            parent: 'Documentos',
            viewPermissions:['previBotView'],
            editPermission:['previBotView'],
            mode: 'VIEW'
          }
      },
      {
        path: '/signaturit',
        component: () => import('./views/eprevision-views/signaturit/SignaturitList.vue'),
        meta: {
          pageTitle: 'Listado signaturit',
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Listado de firmas', active: true},
          ],
          rule: 'user',
          authRequired: true,
          viewPermissions:['signaturitPanelView'],
          editPermission:['signaturitPanelEdit'],
          mode: 'EDIT'
        }
      },

        // !--- ARPA30 -----
        // -- COMPONENTES E-PREVISION - previa restructuración arpa 30 - no borrar
        // ------------------------------
        // Antigua forma de gestionar preguntas de salud - ahora se muestra directamente el doc para rellenar
        // {
        //   path: '/question-types-list',
        //   component: () => import('./views/eprevision-views/question-types/QuestionTypesList.vue'),
        //   meta: {
        //     pageTitle: 'Tipos de preguntas',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Preguntas de salud'},
        //       { title: 'Tipos de preguntas', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['questionTypesEdit'],
        //     editPermission:['questionTypesEdit'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/question-types-detail-new',
        //   component: () => import('./views/eprevision-views/question-types/QuestionTypesDetail.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Lista tipos preguntas', url: '/question-types-list' },
        //       { title: 'Nuevo tipo', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['questionTypesEdit'],
        //     editPermission:['questionTypesEdit'],
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // {
        //   path: '/question-blocks-list',
        //   component: () => import('./views/eprevision-views/question-blocks/QuestionBlocksList.vue'),
        //   meta: {
        //     pageTitle: 'Bloques de preguntas',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Preguntas de salud'},
        //       { title: 'Bloques de preguntas', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['questionBlocksEdit'],
        //     editPermission:['questionBlocksEdit'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/question-blocks-detail-new',
        //   component: () => import('./views/eprevision-views/question-blocks/QuestionBlocksDetail.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Lista bloques preguntas', url: '/question-blocks-list' },
        //       { title: 'Nuevo bloque', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['questionBlocksEdit'],
        //     editPermission:['questionBlocksEdit'],
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // {
        //   path: '/health-questionnaire-list',
        //   component: () => import('./views/eprevision-views/health-questionnaire/HealthQuestionnaireList.vue'),
        //   meta: {
        //     pageTitle: 'Preguntas de salud',
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Preguntas de salud', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['healthQuestionnairesEdit'],
        //     editPermission:['healthQuestionnairesEdit'],
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/health-questionnaire-detail/view/:id',
        //   component: () => import('./views/eprevision-views/health-questionnaire/HealthQuestionnaireDetail.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Lista preguntas', url: '/health-questionnaire-list' },
        //       { title: 'Detalle pregunta', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['healthQuestionnairesEdit'],
        //     editPermission:['healthQuestionnairesEdit'],
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/health-questionnaire-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/health-questionnaire/HealthQuestionnaireDetail.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Lista preguntas', url: '/health-questionnaire-list' },
        //       { title: 'Editar pregunta', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['healthQuestionnairesEdit'],
        //     editPermission:['healthQuestionnairesEdit'],
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/health-questionnaire-detail-new',
        //   component: () => import('./views/eprevision-views/health-questionnaire/HealthQuestionnaireDetail.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Configuración'},
        //       { title: 'Lista preguntas', url: '/health-questionnaire-list' },
        //       { title: 'Nueva pregunta', active: true},
        //     ],
        //     rule: 'user',
        //     viewPermissions:['healthQuestionnairesEdit'],
        //     editPermission:['healthQuestionnairesEdit'],
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // ------------------------------
        // {
        //   path: '/commercial-offers-list',
        //   component: () => import('./views/eprevision-views/commercial-offers/CommercialOffersList.vue'),
        //   meta: {
        //     // breadcrumb: [
        //     //   { title: 'Home', url: '/' },
        //     //   { title: 'Vendor Online' },
        //     //   { title: 'Ofertas Comerciales', active: true }
        //     // ],
        //     rule: 'superAdmin',
        //     authRequired: true,
        //     // pageTitle: 'Ofertas Comerciales',

        //   }
        // },
        // {
        //   path: '/commercial-offer-detail/view/:id',
        //   component: () => import('./views/eprevision-views/commercial-offers/CommercialOfferDetail.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/commercial-offer-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/commercial-offers/CommercialOfferDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/commercial-offer-detail-new',
        //   component: () => import('./views/eprevision-views/commercial-offers/CommercialOfferDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },

        // {
        //   path: '/products-list',
        //   component: () => import('./views/eprevision-views/products/ProductsList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/products-detail/view/:id',
        //   component: () => import('./views/eprevision-views/products/ProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/products-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/products/ProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/products-detail-new',
        //   component: () => import('./views/eprevision-views/products/ProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // {
        //   path: '/extra-products-list',
        //   component: () => import('./views/eprevision-views/extra-products/ExtraProductsList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/extra-products-detail/view/:id',
        //   component: () => import('./views/eprevision-views/extra-products/ExtraProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/extra-products-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/extra-products/ExtraProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/extra-products-detail-new',
        //   component: () => import('./views/eprevision-views/extra-products/ExtraProductsDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // {
        //   path: '/cover-commercial-name-list',
        //   component: () => import('./views/eprevision-views/coverCommercialName/CoverCommercialNameList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/cover-commercial-name-new',
        //   component: () => import('./views/eprevision-views/coverCommercialName/CoverCommercialNameDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },

        // {
        //   path: '/coverage-chart-covers-list',
        //   component: () => import('./views/eprevision-views/coverage-chart-covers/CoverageChartCoversList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },

        // {
        //   path: '/errors-list',
        //   component: () => import('./views/eprevision-views/errors-produced/ErrorsProducedList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/errors-produced-detail/view/:id',
        //   component: () => import('./views/eprevision-views/errors-produced/ErrorsProducedDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/errors-produced-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/errors-produced/ErrorsProducedDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/errors-produced-detail-new',
        //   component: () => import('./views/eprevision-views/errors-produced/ErrorsProducedDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },
        // {
        //   path: '/initial-data-list',
        //   component: () => import('./views/eprevision-views/initial-data/InitialDataList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/initial-data-detail/view/:id',
        //   component: () => import('./views/eprevision-views/initial-data/InitialDataDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/initial-data-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/initial-data/InitialDataDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },

        // {
        //   path: '/company-list',
        //   component: () => import('./views/eprevision-views/company/CompanyList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/company-detail-new',
        //   component: () => import('./views/eprevision-views/company/CompanyDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },


        // {
        //   path: '/cover-depend-list',
        //   component: () => import('./views/eprevision-views/cover-depend/CoverDependList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/cover-depend/view/:id',
        //   component: () => import('./views/eprevision-views/cover-depend/CoverDependDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/cover-depend/edit/:id',
        //   component: () => import('./views/eprevision-views/cover-depend/CoverDependDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },

        // {
        //   path: '/incentives-list',
        //   component: () => import('./views/eprevision-views/incentives/IncentivesList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },
        // {
        //   path: '/incentive-detail/view/:id',
        //   component: () => import('./views/eprevision-views/incentives/IncentiveDetail.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/incentive-detail/edit/:id',
        //   component: () => import('./views/eprevision-views/incentives/IncentiveDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'EDIT'
        //   }
        // },
        // {
        //   path: '/incentive-detail-new',
        //   component: () => import('./views/eprevision-views/incentives/IncentiveDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'NEW'
        //   }
        // },

        // {
        //   path: '/segelevia-incentives',
        //   component: () => import('./views/eprevision-views/segelevia-incentives/SegeleviaIncentivesList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },

        // {
        //   path: '/segelevia-incentives/:code',
        //   component: () => import('./views/eprevision-views/segelevia-incentives/SegeleviaIncentivesDetail.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },
        // {
        //   path: '/segelevia-incentives-collaborator/:collaboratorId/:valorationDate/:code',
        //   component: () => import('./views/eprevision-views/segelevia-incentives-collaborator/SegeleviaIncentivesCollaborator.vue'),
        //   // name:"prueba",
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //     mode: 'VIEW'
        //   }
        // },

        // {
        //   path: '/roles-list',
        //   component: () => import('./views/eprevision-views/roles/RolesList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },

        // {
        //   path: '/permissions-list',
        //   component: () => import('./views/eprevision-views/permissions/PermissionsList.vue'),
        //   meta: {
        //     rule: 'user',
        //     authRequired: true,
        //   }
        // },

      // NO BORRAR - PLANTILLA:
      // ---------------------------------------------------------------------------------
        // {
        //   path: '/dashboard/analytics',
        //   name: 'dashboard-analytics',
        //   component: () => import('./views/DashboardAnalytics.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/dashboard/ecommerce',
        //   name: 'dashboard-ecommerce',
        //   component: () => import('./views/DashboardECommerce.vue'),
        //   meta: {
        //     rule: 'admin'
        //   }
        // },
         // {
        //   path: '/iframe-example/:code',
        //   name: 'iframe-example',
        //   component: () => import('./views/IframeExample.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //   }
        // },


        // // =============================================================================
        // // Application Routes - PLANTILLA: - NO BORRAR
        // // =============================================================================
        // {
        //   path: '/apps/todo',
        //   redirect: '/apps/todo/all',
        //   name: 'todo'
        // },
        // {
        //   path: '/apps/todo/:filter',
        //   component: () => import('./views/apps/todo/Todo.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     parent: 'todo',
        //     no_scroll: true
        //   }
        // },
        // {
        //   path: '/apps/chat',
        //   name: 'chat',
        //   component: () => import('./views/apps/chat/Chat.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     no_scroll: true
        //   }
        // },
        // {
        //   path: '/apps/email',
        //   redirect: '/apps/email/inbox',
        //   name: 'email'
        // },
        // {
        //   path: '/apps/email/:filter',
        //   component: () => import('./views/apps/email/Email.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     parent: 'email',
        //     no_scroll: true
        //   }
        // },
        // {
        //   path: '/apps/calendar/vue-simple-calendar',
        //   name: 'calendar-simple-calendar',
        //   component: () => import('./views/apps/calendar/SimpleCalendar.vue'),
        //   meta: {
        //     rule: 'superAdmin',
        //     no_scroll: true
        //   }
        // },
        // {
        //   path: '/apps/eCommerce/shop',
        //   name: 'ecommerce-shop',
        //   component: () => import('./views/apps/eCommerce/ECommerceShop.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'eCommerce'},
        //       { title: 'Shop', active: true }
        //     ],
        //     pageTitle: 'Shop',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/apps/eCommerce/wish-list',
        //   name: 'ecommerce-wish-list',
        //   component: () => import('./views/apps/eCommerce/ECommerceWishList.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'eCommerce', url:'/apps/eCommerce/shop'},
        //       { title: 'Wish List', active: true }
        //     ],
        //     pageTitle: 'Wish List',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/apps/eCommerce/checkout',
        //   name: 'ecommerce-checkout',
        //   component: () => import('./views/apps/eCommerce/ECommerceCheckout.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'eCommerce', url:'/apps/eCommerce/shop'},
        //       { title: 'Checkout', active: true }
        //     ],
        //     pageTitle: 'Checkout',
        //     rule: 'superAdmin'
        //   }
        // },
        // /*
        //           Below route is for demo purpose
        //           You can use this route in your app
        //             {
        //                 path: '/apps/eCommerce/item/',
        //                 name: 'ecommerce-item-detail-view',
        //                 redirect: '/apps/eCommerce/shop',
        //             }
        //         */
        // {
        //   path: '/apps/eCommerce/item/',
        //   redirect: '/apps/eCommerce/item/5546604'
        // },
        // {
        //   path: '/apps/eCommerce/item/:item_id',
        //   name: 'ecommerce-item-detail-view',
        //   component: () => import('./views/apps/eCommerce/ECommerceItemDetailView.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'eCommerce'},
        //       { title: 'Shop', url: {name: 'ecommerce-shop'} },
        //       { title: 'Item Details', active: true }
        //     ],
        //     parent: 'ecommerce-item-detail-view',
        //     pageTitle: 'Item Details',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/apps/user/user-list',
        //   name: 'app-user-list',
        //   component: () => import('@/views/apps/user/user-list/UserList.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'User' },
        //       { title: 'List', active: true }
        //     ],
        //     pageTitle: 'User List',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/apps/user/user-view/:userId',
        //   name: 'app-user-view',
        //   component: () => import('@/views/apps/user/UserView.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'User' },
        //       { title: 'View', active: true }
        //     ],
        //     pageTitle: 'User View',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/apps/user/user-edit/:userId',
        //   name: 'app-user-edit',
        //   component: () => import('@/views/apps/user/user-edit/UserEdit.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'User' },
        //       { title: 'Edit', active: true }
        //     ],
        //     pageTitle: 'User Edit',
        //     rule: 'superAdmin'
        //   }
        // },
        // // =============================================================================
        // // UI ELEMENTS - NO BORRAR PLANTILLA
        // // =============================================================================
        // {
        //   path: '/ui-elements/data-list/list-view',
        //   name: 'data-list-list-view',
        //   component: () => import('@/views/ui-elements/data-list/list-view/DataListListView.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Data List'},
        //       { title: 'List View', active: true }
        //     ],
        //     pageTitle: 'List View',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/data-list/thumb-view',
        //   name: 'data-list-thumb-view',
        //   component: () => import('@/views/ui-elements/data-list/thumb-view/DataListThumbView.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Data List'},
        //       { title: 'Thumb View', active: true }
        //     ],
        //     pageTitle: 'Thumb View',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/grid/vuesax',
        //   name: 'grid-vuesax',
        //   component: () => import('@/views/ui-elements/grid/vuesax/GridVuesax.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Grid'},
        //       { title: 'Vuesax', active: true }
        //     ],
        //     pageTitle: 'Grid',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/grid/tailwind',
        //   name: 'grid-tailwind',
        //   component: () => import('@/views/ui-elements/grid/tailwind/GridTailwind.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Grid'},
        //       { title: 'Tailwind', active: true }
        //     ],
        //     pageTitle: 'Tailwind Grid',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/colors',
        //   name: 'colors',
        //   component: () => import('./views/ui-elements/colors/Colors.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Colors', active: true }
        //     ],
        //     pageTitle: 'Colors',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/card/basic',
        //   name: 'basic-cards',
        //   component: () => import('./views/ui-elements/card/CardBasic.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Card' },
        //       { title: 'Basic Cards', active: true }
        //     ],
        //     pageTitle: 'Basic Cards',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/card/statistics',
        //   name: 'statistics-cards',
        //   component: () => import('./views/ui-elements/card/CardStatistics.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Card' },
        //       { title: 'Statistics Cards', active: true }
        //     ],
        //     pageTitle: 'Statistics Card',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/card/analytics',
        //   name: 'analytics-cards',
        //   component: () => import('./views/ui-elements/card/CardAnalytics.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Card' },
        //       { title: 'Analytics Card', active: true }
        //     ],
        //     pageTitle: 'Analytics Card',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/card/card-actions',
        //   name: 'card-actions',
        //   component: () => import('./views/ui-elements/card/CardActions.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Card' },
        //       { title: 'Card Actions', active: true }
        //     ],
        //     pageTitle: 'Card Actions',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/card/card-colors',
        //   name: 'card-colors',
        //   component: () => import('./views/ui-elements/card/CardColors.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Card' },
        //       { title: 'Card Colors', active: true }
        //     ],
        //     pageTitle: 'Card Colors',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/table',
        //   name: 'table',
        //   component: () => import('./views/ui-elements/table/Table.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Table', active: true }
        //     ],
        //     pageTitle: 'Table',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/ui-elements/ag-grid-table',
        //   name: 'ag-grid-table',
        //   component: () => import('./views/ui-elements/ag-grid-table/AgGridTable.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'agGrid Table', active: true }
        //     ],
        //     pageTitle: 'agGrid Table',
        //     rule: 'superAdmin'
        //   }
        // },

        // // =============================================================================
        // // COMPONENT ROUTES -- NO BORRAR - PLANTILLA
        // // =============================================================================
        // {
        //   path: '/components/alert',
        //   name: 'component-alert',
        //   component: () => import('@/views/components/vuesax/alert/Alert.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Alert', active: true }
        //     ],
        //     pageTitle: 'Alert',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/avatar',
        //   name: 'component-avatar',
        //   component: () => import('@/views/components/vuesax/avatar/Avatar.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Avatar', active: true }
        //     ],
        //     pageTitle: 'Avatar',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/breadcrumb',
        //   name: 'component-breadcrumb',
        //   component: () => import('@/views/components/vuesax/breadcrumb/Breadcrumb.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Breadcrumb', active: true }
        //     ],
        //     pageTitle: 'Breadcrumb',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/button',
        //   name: 'component-button',
        //   component: () => import('@/views/components/vuesax/button/Button.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Button', active: true }
        //     ],
        //     pageTitle: 'Button',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/button-group',
        //   name: 'component-button-group',
        //   component: () => import('@/views/components/vuesax/button-group/ButtonGroup.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Button Group', active: true }
        //     ],
        //     pageTitle: 'Button Group',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/chip',
        //   name: 'component-chip',
        //   component: () => import('@/views/components/vuesax/chip/Chip.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Chip', active: true }
        //     ],
        //     pageTitle: 'Chip',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/collapse',
        //   name: 'component-collapse',
        //   component: () => import('@/views/components/vuesax/collapse/Collapse.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Collapse', active: true }
        //     ],
        //     pageTitle: 'Collapse',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/dialogs',
        //   name: 'component-dialog',
        //   component: () => import('@/views/components/vuesax/dialogs/Dialogs.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Dialogs', active: true }
        //     ],
        //     pageTitle: 'Dialogs',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/divider',
        //   name: 'component-divider',
        //   component: () => import('@/views/components/vuesax/divider/Divider.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Divider', active: true }
        //     ],
        //     pageTitle: 'Divider',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/dropdown',
        //   name: 'component-drop-down',
        //   component: () => import('@/views/components/vuesax/dropdown/Dropdown.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Dropdown', active: true }
        //     ],
        //     pageTitle: 'Dropdown',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/list',
        //   name: 'component-list',
        //   component: () => import('@/views/components/vuesax/list/List.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'List', active: true }
        //     ],
        //     pageTitle: 'List',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/loading',
        //   name: 'component-loading',
        //   component: () => import('@/views/components/vuesax/loading/Loading.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Loading', active: true }
        //     ],
        //     pageTitle: 'Loading',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/navbar',
        //   name: 'component-navbar',
        //   component: () => import('@/views/components/vuesax/navbar/Navbar.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Navbar', active: true }
        //     ],
        //     pageTitle: 'Navbar',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/notifications',
        //   name: 'component-notifications',
        //   component: () => import('@/views/components/vuesax/notifications/Notifications.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Notifications', active: true }
        //     ],
        //     pageTitle: 'Notifications',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/pagination',
        //   name: 'component-pagination',
        //   component: () => import('@/views/components/vuesax/pagination/Pagination.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Pagination', active: true }
        //     ],
        //     pageTitle: 'Pagination',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/popup',
        //   name: 'component-popup',
        //   component: () => import('@/views/components/vuesax/popup/Popup.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Popup', active: true }
        //     ],
        //     pageTitle: 'Popup',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/progress',
        //   name: 'component-progress',
        //   component: () => import('@/views/components/vuesax/progress/Progress.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Progress', active: true }
        //     ],
        //     pageTitle: 'Progress',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/sidebar',
        //   name: 'component-sidebar',
        //   component: () => import('@/views/components/vuesax/sidebar/Sidebar.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Sidebar', active: true }
        //     ],
        //     pageTitle: 'Sidebar',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/slider',
        //   name: 'component-slider',
        //   component: () => import('@/views/components/vuesax/slider/Slider.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Slider', active: true }
        //     ],
        //     pageTitle: 'Slider',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/tabs',
        //   name: 'component-tabs',
        //   component: () => import('@/views/components/vuesax/tabs/Tabs.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Tabs', active: true }
        //     ],
        //     pageTitle: 'Tabs',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/tooltip',
        //   name: 'component-tooltip',
        //   component: () => import('@/views/components/vuesax/tooltip/Tooltip.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Tooltip', active: true }
        //     ],
        //     pageTitle: 'Tooltip',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/components/upload',
        //   name: 'component-upload',
        //   component: () => import('@/views/components/vuesax/upload/Upload.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Components' },
        //       { title: 'Upload', active: true }
        //     ],
        //     pageTitle: 'Upload',
        //     rule: 'superAdmin'
        //   }
        // },


        // // =============================================================================
        // // FORMS -- NO BORRAR
        // // =============================================================================
        // // =============================================================================
        // // FORM ELEMENTS
        // // =============================================================================
        // {
        //   path: '/forms/form-elements/select',
        //   name: 'form-element-select',
        //   component: () => import('./views/forms/form-elements/select/Select.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Select', active: true }
        //     ],
        //     pageTitle: 'Select',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/switch',
        //   name: 'form-element-switch',
        //   component: () => import('./views/forms/form-elements/switch/Switch.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Switch', active: true }
        //     ],
        //     pageTitle: 'Switch',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/checkbox',
        //   name: 'form-element-checkbox',
        //   component: () => import('./views/forms/form-elements/checkbox/Checkbox.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Checkbox', active: true }
        //     ],
        //     pageTitle: 'Checkbox',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/radio',
        //   name: 'form-element-radio',
        //   component: () => import('./views/forms/form-elements/radio/Radio.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Radio', active: true }
        //     ],
        //     pageTitle: 'Radio',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/input',
        //   name: 'form-element-input',
        //   component: () => import('./views/forms/form-elements/input/Input.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Input', active: true }
        //     ],
        //     pageTitle: 'Input',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/number-input',
        //   name: 'form-element-number-input',
        //   component: () => import('./views/forms/form-elements/number-input/NumberInput.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Number Input', active: true }
        //     ],
        //     pageTitle: 'Number Input',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-elements/textarea',
        //   name: 'form-element-textarea',
        //   component: () => import('./views/forms/form-elements/textarea/Textarea.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Form Elements' },
        //       { title: 'Textarea', active: true }
        //     ],
        //     pageTitle: 'Textarea',
        //     rule: 'superAdmin'
        //   }
        // },
        // // -------------------------------------------------------------------------------------------------------------------------------------------
        // {
        //   path: '/forms/form-layouts',
        //   name: 'forms-form-layouts',
        //   component: () => import('@/views/forms/FormLayouts.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Forms' },
        //       { title: 'Form Layouts', active: true }
        //     ],
        //     pageTitle: 'Form Layouts',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-wizard',
        //   name: 'extra-component-form-wizard',
        //   component: () => import('@/views/forms/form-wizard/FormWizard.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Form Wizard', active: true }
        //     ],
        //     pageTitle: 'Form Wizard',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-validation',
        //   name: 'extra-component-form-validation',
        //   component: () => import('@/views/forms/form-validation/FormValidation.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Form Validation', active: true }
        //     ],
        //     pageTitle: 'Form Validation',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/forms/form-input-group',
        //   name: 'extra-component-form-input-group',
        //   component: () => import('@/views/forms/form-input-group/FormInputGroup.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Form Input Group', active: true }
        //     ],
        //     pageTitle: 'Form Input Group',
        //     rule: 'superAdmin'
        //   }
        // },

        // // =============================================================================
        // // Pages Routes - NO BORRAR
        // // =============================================================================
        // {
        //   path: '/pages/profile',
        //   name: 'page-profile',
        //   component: () => import('@/views/pages/Profile.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'Profile', active: true }
        //     ],
        //     pageTitle: 'Profile',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/user-settings',
        //   name: 'page-user-settings',
        //   component: () => import('@/views/pages/user-settings/UserSettings.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'User Settings', active: true }
        //     ],
        //     pageTitle: 'Settings',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/faq',
        //   name: 'page-faq',
        //   component: () => import('@/views/pages/Faq.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'FAQ', active: true }
        //     ],
        //     pageTitle: 'FAQ',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/knowledge-base',
        //   name: 'page-knowledge-base',
        //   component: () => import('@/views/pages/KnowledgeBase.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'KnowledgeBase', active: true }
        //     ],
        //     pageTitle: 'KnowledgeBase',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/knowledge-base/category',
        //   name: 'page-knowledge-base-category',
        //   component: () => import('@/views/pages/KnowledgeBaseCategory.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
        //       { title: 'Category', active: true }
        //     ],
        //     parent: 'page-knowledge-base',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/knowledge-base/category/question',
        //   name: 'page-knowledge-base-category-question',
        //   component: () => import('@/views/pages/KnowledgeBaseCategoryQuestion.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
        //       { title: 'Category', url: '/pages/knowledge-base/category' },
        //       { title: 'Question', active: true }
        //     ],
        //     parent: 'page-knowledge-base',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/search',
        //   name: 'page-search',
        //   component: () => import('@/views/pages/Search.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'Search', active: true }
        //     ],
        //     pageTitle: 'Search',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/invoice',
        //   name: 'page-invoice',
        //   component: () => import('@/views/pages/Invoice.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Pages' },
        //       { title: 'Invoice', active: true }
        //     ],
        //     pageTitle: 'Invoice',
        //     rule: 'superAdmin'
        //   }
        // },

        // // =============================================================================
        // // CHARTS & MAPS  - NO BORRAR
        // // =============================================================================
        // {
        //   path: '/charts-and-maps/charts/apex-charts',
        //   name: 'extra-component-charts-apex-charts',
        //   component: () => import('@/views/charts-and-maps/charts/apex-charts/ApexCharts.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Charts & Maps' },
        //       { title: 'Apex Charts', active: true }
        //     ],
        //     pageTitle: 'Apex Charts',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/charts-and-maps/charts/chartjs',
        //   name: 'extra-component-charts-chartjs',
        //   component: () => import('@/views/charts-and-maps/charts/chartjs/Chartjs.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Charts & Maps' },
        //       { title: 'chartjs', active: true }
        //     ],
        //     pageTitle: 'chartjs',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/charts-and-maps/charts/echarts',
        //   name: 'extra-component-charts-echarts',
        //   component: () => import('@/views/charts-and-maps/charts/echarts/Echarts.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Charts & Maps' },
        //       { title: 'echarts', active: true }
        //     ],
        //     pageTitle: 'echarts',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/charts-and-maps/maps/google-map',
        //   name: 'extra-component-maps-google-map',
        //   component: () => import('@/views/charts-and-maps/maps/google-map/GoogleMap.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Charts & Maps' },
        //       { title: 'Google Map', active: true }
        //     ],
        //     pageTitle: 'Google Map',
        //     rule: 'superAdmin'
        //   }
        // },


        // // =============================================================================
        // // EXTENSIONS - NO BORRAR
        // // =============================================================================
        // {
        //   path: '/extensions/select',
        //   name: 'extra-component-select',
        //   component: () => import('@/views/components/extra-components/select/Select.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Select', active: true }
        //     ],
        //     pageTitle: 'Select',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/quill-editor',
        //   name: 'extra-component-quill-editor',
        //   component: () => import('@/views/components/extra-components/quill-editor/QuillEditor.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Quill Editor', active: true }
        //     ],
        //     pageTitle: 'Quill Editor',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/drag-and-drop',
        //   name: 'extra-component-drag-and-drop',
        //   component: () => import('@/views/components/extra-components/drag-and-drop/DragAndDrop.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Drag & Drop', active: true }
        //     ],
        //     pageTitle: 'Drag & Drop',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/datepicker',
        //   name: 'extra-component-datepicker',
        //   component: () => import('@/views/components/extra-components/datepicker/Datepicker.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Datepicker', active: true }
        //     ],
        //     pageTitle: 'Datepicker',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/datetime-picker',
        //   name: 'extra-component-datetime-picker',
        //   component: () => import('@/views/components/extra-components/datetime-picker/DatetimePicker.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extra Components' },
        //       { title: 'Datetime Picker', active: true }
        //     ],
        //     pageTitle: 'Datetime Picker',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/access-control',
        //   name: 'extra-component-access-control',
        //   component: () => import('@/views/components/extra-components/access-control/AccessControl.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Access Control', active: true }
        //     ],
        //     pageTitle: 'Access Control',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/i18n',
        //   name: 'extra-component-i18n',
        //   component: () => import('@/views/components/extra-components/I18n.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'I18n', active: true }
        //     ],
        //     pageTitle: 'I18n',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/carousel',
        //   name: 'extra-component-carousel',
        //   component: () => import('@/views/components/extra-components/carousel/Carousel.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Carousel', active: true }
        //     ],
        //     pageTitle: 'Carousel',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/clipboard',
        //   name: 'extra-component-clipboard',
        //   component: () => import('@/views/components/extra-components/clipboard/Clipboard.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Clipboard', active: true }
        //     ],
        //     pageTitle: 'Clipboard',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/context-menu',
        //   name: 'extra-component-context-menu',
        //   component: () => import('@/views/components/extra-components/context-menu/ContextMenu.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Context Menu', active: true }
        //     ],
        //     pageTitle: 'Context Menu',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/star-ratings',
        //   name: 'extra-component-star-ratings',
        //   component: () => import('@/views/components/extra-components/star-ratings/StarRatings.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Star Ratings', active: true }
        //     ],
        //     pageTitle: 'Star Ratings',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/autocomplete',
        //   name: 'extra-component-autocomplete',
        //   component: () => import('@/views/components/extra-components/autocomplete/Autocomplete.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Autocomplete', active: true }
        //     ],
        //     pageTitle: 'Autocomplete',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/extensions/tree',
        //   name: 'extra-component-tree',
        //   component: () => import('@/views/components/extra-components/tree/Tree.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Tree', active: true }
        //     ],
        //     pageTitle: 'Tree',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/import-export/import',
        //   name: 'import-excel',
        //   component: () => import('@/views/components/extra-components/import-export/Import.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Import/Export' },
        //       { title: 'Import', active: true }
        //     ],
        //     pageTitle: 'Import Excel',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/import-export/export',
        //   name: 'export-excel',
        //   component: () => import('@/views/components/extra-components/import-export/Export.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Import/Export' },
        //       { title: 'Export', active: true }
        //     ],
        //     pageTitle: 'Export Excel',
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/import-export/export-selected',
        //   name: 'export-excel-selected',
        //   component: () => import('@/views/components/extra-components/import-export/ExportSelected.vue'),
        //   meta: {
        //     breadcrumb: [
        //       { title: 'Home', url: '/' },
        //       { title: 'Extensions' },
        //       { title: 'Import/Export' },
        //       { title: 'Export Selected', active: true }
        //     ],
        //     pageTitle: 'Export Excel',
        //     rule: 'superAdmin'
        //   }
        // }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [

        // =============================================================================
        // E-PREVISION Pages:
        // =============================================================================
        {
          path: '/e-prevision/login-eprevision',
          name: 'login-eprevision',
          component: () => import('@/views/pages/e-prevision/login/Login.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },
        {
          path: '/e-prevision/login-eprevision/second-step',
          name: 'login-eprevision-second',
          component: () => import('@/views/pages/e-prevision/login/LoginSMSCode.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },
        {
          path: '/e-prevision/temporal-2fa-access',
          name: 'temporal-access',
          component: () => import('@/views/pages/e-prevision/login/LoginTemporalAccess.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/sso',
          name: 'sso',
          component: () => import('@/views/pages/e-prevision/Sso.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/e-prevision/forgot-password',
          name: 'eprevision-forgot-password',
          component: () => import('@/views/pages/e-prevision/ForgotPassword.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/e-prevision/reset-password/:code',
          name: 'eprevision-reset-password',
          component: () => import('@/views/pages/e-prevision/ResetPassword.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/e-prevision/not-authorized',
          name: 'eprevision-not-authorized',
          component: () => import('@/views/pages/e-prevision/NotAuthorized.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/e-prevision/wait-for-recover-email',
          name: 'eprevision-wait-recover-email',
          component: () => import('@/views/pages/e-prevision/login/LoginWaitRecoverEmail.vue'),
          meta: {
            rule: 'user',
            authRequired: false
          }
        },

        {
          path: '/e-prevision/error-404',
          name: 'eprevision-error-404',
          component: () => import('@/views/pages/e-prevision/Error404.vue'),
          meta: {
            rule: 'user'
          }
        },

        {
          path: '/e-prevision/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/e-prevision/Maintenance.vue'),
          meta: {
            rule: 'user'
          }
        }


        // // =============================================================================
        // // PAGES - NO BORRAR PLANTILLA
        // // =============================================================================

        // {
        //   path: '/callback',
        //   name: 'auth-callback',
        //   component: () => import('@/views/Callback.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },

        // {
        //   path: '/pages/login',
        //   name: 'page-login',
        //   component: () => import('@/views/pages/login/Login.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/register',
        //   name: 'page-register',
        //   component: () => import('@/views/pages/register/Register.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },

        // {
        //   path: '/pages/forgot-password',
        //   name: 'page-forgot-password',
        //   component: () => import('@/views/pages/ForgotPassword.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/reset-password',
        //   name: 'page-reset-password',
        //   component: () => import('@/views/pages/ResetPassword.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/lock-screen',
        //   name: 'page-lock-screen',
        //   component: () => import('@/views/pages/LockScreen.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/comingsoon',
        //   name: 'page-coming-soon',
        //   component: () => import('@/views/pages/ComingSoon.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/error-404',
        //   name: 'page-error-404',
        //   component: () => import('@/views/pages/Error404.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/error-500',
        //   name: 'page-error-500',
        //   component: () => import('@/views/pages/Error500.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/not-authorized',
        //   name: 'page-not-authorized',
        //   component: () => import('@/views/pages/NotAuthorized.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // },
        // {
        //   path: '/pages/maintenance',
        //   name: 'page-maintenance',
        //   component: () => import('@/views/pages/Maintenance.vue'),
        //   meta: {
        //     rule: 'superAdmin'
        //   }
        // }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/e-prevision/error-404'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

// export default router
// If generic pages - nothing done in between -> next()

// If user is logged in with userInfo in localStorage & router needs to be authorized to access (to.meta.authRequired):
      // If home or page which does not have viewPermissions associated -> nothing done -> next()
      // If page with viewPermissions associated -> check against user permissions -> if permissions, next(); else, unauthorized page

// If router does NOT need to be authorized to access (to.meta.authRequired) -> next()

// Other cases (unauthorized) -> not-authorized & delete localStorage


router.beforeEach((to, from, next) => {
    // If basic pages - nothing done in between, sent over.
    if (
      to.path === "/e-prevision/login-eprevision" ||
      to.path === "/e-prevision/forgot-password" ||
      to.path === "/e-prevision/error-404" ||
      to.path === "/e-prevision/not-authorized" ||
      to.path === "/sso"
        //         // to.path === "/pages/error-500" ||
        //         // to.path === "/pages/register" ||
        //         // to.path === "/callback" ||
        //         // to.path === "/pages/comingsoon"
      ) {
        return next();

      // If user is logged in with userInfo in localStorage & router needs to be authorized to access:
      } else if (auth.isAuthenticated() && to.meta.authRequired) {

        let routerPermissions = router.options.routes[0].children.filter(a => a.path == to.matched[1].path)[0].meta.viewPermissions

        // If home or page which does not have viewPermissions associated
        if (routerPermissions == undefined) {
            return next();

        // If page with viewPermissions associated -> check against user permissions
        } else {

          const index = auth.hasViewPermissionCode(routerPermissions)
          if (index >= 0) {
           return next();

          } else {
            router.push({ path: '/e-prevision/not-authorized' }).catch()
          }
        }
      } else if (!to.meta.authRequired){
          return next();

      } else {
        oauth2.deleteLocalUser()
        router.push({ path: '/e-prevision/login-eprevision' }).catch()

        return next();
      }
  })

export default router